import { LogOut, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/hooks/use-auth';
import { cn } from '@/lib/utils';
import { pathToAuthLogout } from '@/navigation/navigate';
import { iconSize } from '@/theme/size-tokens';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

type SidebarUserProfileProps = {
  collapsed: boolean;
};

export default function SidebarUserProfile({
  collapsed,
}: SidebarUserProfileProps) {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="shrink-0">
      <Popover>
        <PopoverTrigger asChild>
          <button
            className={cn(
              'flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left transition-[colors,padding] duration-200',
              'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
              collapsed && 'justify-center gap-0'
            )}
            aria-label="User menu"
          >
            <div className="bg-sidebar-accent text-sidebar-accent-foreground flex size-8 shrink-0 items-center justify-center rounded-full">
              <User className={iconSize('sm')} />
            </div>
            <span
              className={cn(
                'flex-1 truncate overflow-hidden whitespace-nowrap text-sm transition-[opacity,max-width] duration-200',
                collapsed ? 'max-w-0 opacity-0' : 'max-w-40 opacity-100'
              )}
            >
              {user?.email ?? ''}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent
          side="right"
          align="end"
          sideOffset={8}
          className="w-64 p-0"
        >
          {/* Profile info */}
          <div className="px-4 py-3">
            {user?.name && <p className="text-sm font-medium">{user.name}</p>}
            <p className="text-muted-foreground truncate text-xs">
              {user?.email ?? ''}
            </p>
          </div>

          <div className="border-t" />

          {/* Actions */}
          <div className="p-1">
            <button
              className="text-error hover:bg-error/10 flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors"
              onClick={() => navigate(pathToAuthLogout())}
            >
              <LogOut className={iconSize('sm')} />
              Logout
            </button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
